import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import AuthCallback from "./pages/AuthCallback";
import LinkedInAuthCallback from "./pages/LinkedInAuthCallback";
import Unsubscribe from "./pages/Unsubscribe";
import StudentDashboard from "./pages/StudentDashboard";
import CompanyDashboard from "./pages/CompanyDashboard";
import InstituteDashboard from "./pages/InstituteDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import PostingsPublic from "./pages/PostingsPublic";
import JobDetailPublic from "./pages/JobDetailPublic";
import LMSPublic from "./pages/LMSPublic";
import LMSLab from "./pages/LMSLab";
import StudentsPublic from "./pages/StudentsPublic";
import ProfessionalsPublic from "./pages/ProfessionalsPublic";
import CompaniesPublic from "./pages/CompaniesPublic";
import InstitutionsPublic from "./pages/InstitutionsPublic";
import LegalPage, { LegalIndex } from "./legal/LegalPage";
import CookieConsent from "./components/CookieConsent";
import InstallAppBanner from "./components/InstallAppBanner";
import VerifyCert from "./pages/VerifyCert";
import AdminPortalLogin from "./pages/AdminPortalLogin";
import AssessmentRunner from "./pages/AssessmentRunner";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted" data-testid="loading-screen">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={`/${user.role}/dashboard`} replace />;
  return children;
}

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={`/${user.role}/dashboard`} replace />;
}

function AppRouter() {
  const location = useLocation();
  // Emergent Google callback: #session_id=... — intercept during render (not useEffect)
  if (typeof window !== "undefined" && location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/admin-portal" element={<AdminPortalLogin />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/opportunities" element={<PostingsPublic />} />
      <Route path="/jobs/:pid" element={<JobDetailPublic />} />
      <Route path="/for-students" element={<StudentsPublic />} />
      <Route path="/for-professionals" element={<ProfessionalsPublic />} />
      <Route path="/for-institutions" element={<InstitutionsPublic />} />
      <Route path="/for-universities" element={<Navigate to="/for-institutions?tab=university" replace />} />
      <Route path="/for-institutes" element={<Navigate to="/for-institutions?tab=college" replace />} />
      <Route path="/for-companies" element={<CompaniesPublic />} />
      <Route path="/verify/:certId" element={<VerifyCert />} />
      <Route path="/auth/linkedin/callback" element={<LinkedInAuthCallback />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/assess/:token" element={<AssessmentRunner />} />
      <Route path="/lms" element={<LMSPublic />} />
      <Route path="/lms/labs/:labId" element={<LMSLab />} />
      <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
      <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
      <Route path="/legal" element={<LegalIndex />} />
      <Route path="/legal/:slug" element={<LegalPage />} />
      <Route path="/dashboard" element={<RoleRedirect />} />
      <Route path="/student/*" element={<Protected roles={["student", "working_pro"]}><StudentDashboard /></Protected>} />
      <Route path="/working_pro/*" element={<Protected roles={["working_pro"]}><StudentDashboard /></Protected>} />
      <Route path="/company/*" element={<Protected roles={["company"]}><CompanyDashboard /></Protected>} />
      <Route path="/institution/*" element={<Protected roles={["institution"]}><InstituteDashboard /></Protected>} />
      {/* Legacy redirect — old role name routed via /institute/* */}
      <Route path="/institute/*" element={<Navigate to="/institution/dashboard" replace />} />
      <Route path="/admin/*" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ThemeProvider>
        <AuthProvider>
          <AppRouter />
          <CookieConsent />
          <InstallAppBanner />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export { Link };
